"use client";

import { Brush, Pencil, Scissors, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Services() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const mediums = [{
    icon: Brush,
    title: "Strategic Consulting",
    description: "Expert guidance to refine your business model, optimize operations, and drive sustainable growth.",
    linkLabel: "Explore consulting",
    to: "/OurServicesPage",
    tilt: "-rotate-1",
    mobileTilt: "-rotate-[0.5deg]",
    mostLoved: false
  }, {
    icon: Pencil,
    title: "Brand Identity",
    description: "Crafting compelling visual narratives and messaging that resonate deeply with your target audience.",
    linkLabel: "View branding services",
    to: "/OurServicesPage",
    tilt: "rotate-1",
    mobileTilt: "rotate-[0.5deg]",
    mostLoved: false
  }, {
    icon: Sparkles,
    title: "Digital Transformation",
    description: "Modernizing your digital infrastructure to enhance efficiency and deliver seamless user experiences.",
    linkLabel: "See digital solutions",
    to: "/OurServicesPage",
    tilt: "-rotate-[0.5deg]",
    mobileTilt: "-rotate-[0.25deg]",
    mostLoved: true
  }, {
    icon: Scissors,
    title: "Content Strategy",
    description: "Developing high-impact content frameworks that build authority and foster meaningful engagement.",
    linkLabel: "Discover content services",
    to: "/OurServicesPage",
    tilt: "rotate-[1.5deg]",
    mobileTilt: "rotate-[0.5deg]",
    mostLoved: false
  }];
  return <section data-section-id="20487" id="our-ad-hoc-services" className="bg-background text-foreground py-20 md:py-28 px-5 md:px-8 font-default">
      <div className="max-w-[1240px] mx-auto">
        {}
        <div ref={intro.ref} className={`max-w-[720px] mx-auto text-center transition-all duration-700 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="font-default font-bold uppercase tracking-[0.12em] text-[0.8125rem] md:text-[0.9375rem] text-muted-foreground mb-5">
            Our Professional Expertise
          </p>
          <h2 className="font-default font-medium text-foreground leading-[1.08] tracking-[-0.015em]" style={{
          fontSize: "clamp(2.125rem, 4vw, 3.75rem)"
        }}>
            Tailored solutions for{" "}
            <span className="relative inline-block italic">
              <span className="relative z-10">your success</span>
              <svg aria-hidden="true" viewBox="0 0 240 60" className="absolute left-[-8px] right-[-8px] top-[-6px] bottom-[-6px] w-[calc(100%+16px)] h-[calc(100%+12px)] pointer-events-none" fill="none">
                <path d="M 18 30 C 18 12, 70 6, 120 8 C 180 10, 224 18, 222 32 C 220 48, 160 54, 110 52 C 60 50, 20 44, 22 30" stroke="var(--primary)" strokeWidth="2.5" strokeLinecap="round" fill="none" pathLength={1} strokeDasharray={1} strokeDashoffset={intro.isVisible ? 0 : 1} style={{
                transition: "stroke-dashoffset 800ms ease-out 300ms"
              }} />
              </svg>
            </span>
          </h2>
          <p className="font-default text-muted-foreground mt-6 leading-[1.6]" style={{
          fontSize: "clamp(1.0625rem, 1.3vw, 1.25rem)"
        }}>
            We provide specialized services designed to elevate your business performance and help you achieve your long-term strategic objectives.
          </p>
        </div>

        {}
        <div ref={grid.ref} className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {mediums.map((medium, i) => {
          const Icon = medium.icon;
          return <div key={medium.title} data-index={i} className={`md:col-span-3 transition-all duration-700 ease-out ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: `${i * 80}ms`
          }}>
                <Card className={`group relative h-full bg-card text-card-foreground py-7 px-7 gap-5 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 ease-out hover:-translate-y-1 ${medium.mobileTilt} md:${medium.tilt} hover:rotate-0 md:hover:rotate-0`}>
                  {}
                  {medium.mostLoved && <div className="absolute -top-3 -right-2 md:-top-4 md:-right-3 flex items-start gap-1 pointer-events-none">
                      <span className="font-default italic text-primary text-sm md:text-base mt-3 -mr-1 whitespace-nowrap" style={{
                  transform: "rotate(-8deg)"
                }}>
                        featured
                      </span>
                      <svg aria-hidden="true" viewBox="0 0 32 32" className="w-7 h-7 md:w-9 md:h-9" fill="none">
                        <path d="M16 4 L16 28 M4 16 L28 16 M7 7 L25 25 M25 7 L7 25" stroke="var(--primary)" strokeWidth="2.5" strokeLinecap="round" pathLength={1} strokeDasharray={1} strokeDashoffset={grid.isVisible ? 0 : 1} style={{
                    transition: `stroke-dashoffset 700ms ease-out ${500 + i * 80}ms`
                  }} />
                      </svg>
                    </div>}

                  {}
                  <div className="flex items-center justify-start">
                    <Icon className="w-14 h-14 text-primary transition-transform duration-300 group-hover:rotate-[8deg]" strokeWidth={1.75} />
                  </div>

                  {}
                  <h3 className="font-default font-semibold text-foreground leading-[1.15] tracking-[-0.01em]" style={{
                fontSize: "clamp(1.5rem, 2.2vw, 2rem)"
              }}>
                    {medium.title}
                  </h3>

                  {}
                  <p className="font-default text-muted-foreground leading-[1.65] text-[0.9375rem] md:text-[1rem]">
                    {medium.description}
                  </p>

                  {}
                  <Link to={medium.to} className="inline-flex items-center gap-1.5 mt-2 font-default font-bold text-foreground text-sm relative w-fit">
                    <span className="relative">
                      {medium.linkLabel}
                      <svg aria-hidden="true" viewBox="0 0 200 10" preserveAspectRatio="none" className="absolute left-0 right-0 -bottom-1 w-full h-[6px] pointer-events-none" fill="none">
                        <path d="M 2 6 C 30 2, 60 8, 100 5 C 140 2, 170 7, 198 4" stroke="var(--primary)" strokeWidth="1.75" strokeLinecap="round" fill="none" />
                      </svg>
                    </span>
                    <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={2} />
                  </Link>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}